(ns practitest.frontend.components2.dashboard.charts.tooltip
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.common :as c]))

(defn- tooltip-bg-path-d
  [w h r aw ah arrow-pos side]
  ;; side = :left | :right | :bottom
  ;; arrow-pos is the center of the arrow along the edge it sits on,
  ;; relative to the tooltip's top-left corner:
  ;;   Y axis for :left/:right (arrow on a vertical edge)
  ;;   X axis for :bottom      (arrow on the horizontal edge)
  (let [half-aw (/ aw 2)]
    (case side
      :right
      (let [arrow-cy (-> arrow-pos (max (+ r half-aw)) (min (- h r half-aw)))]
        (str "M " r ",0 "
             "H " (- w r) " "
             "A " r "," r " 0 0,1 " w "," r " "
             "V " (- arrow-cy half-aw) " "
             "L " (+ w ah) "," arrow-cy " "
             "L " w "," (+ arrow-cy half-aw) " "
             "V " (- h r) " "
             "A " r "," r " 0 0,1 " (- w r) "," h " "
             "H " r " "
             "A " r "," r " 0 0,1 0," (- h r) " "
             "V " r " "
             "A " r "," r " 0 0,1 " r ",0 Z"))

      :bottom
      (let [arrow-cx (-> arrow-pos (max (+ r half-aw)) (min (- w r half-aw)))]
        (str "M " r ",0 "
             "H " (- w r) " "
             "A " r "," r " 0 0,1 " w "," r " "
             "V " (- h r) " "
             "A " r "," r " 0 0,1 " (- w r) "," h " "
             "H " (+ arrow-cx half-aw) " "
             "L " arrow-cx "," (+ h ah) " "
             "L " (- arrow-cx half-aw) "," h " "
             "H " r " "
             "A " r "," r " 0 0,1 0," (- h r) " "
             "V " r " "
             "A " r "," r " 0 0,1 " r ",0 Z"))

      ;; :left (default)
      (let [arrow-cy (-> arrow-pos (max (+ r half-aw)) (min (- h r half-aw)))]
        (str "M " r ",0 "
             "H " (- w r) " "
             "A " r "," r " 0 0,1 " w "," r " "
             "V " (- h r) " "
             "A " r "," r " 0 0,1 " (- w r) "," h " "
             "H " r " "
             "A " r "," r " 0 0,1 0," (- h r) " "
             "V " (+ arrow-cy half-aw) " "
             "L " (- ah) "," arrow-cy " "
             "L " 0 "," (- arrow-cy half-aw) " "
             "V " r " "
             "A " r "," r " 0 0,1 " r ",0 Z")))))

(defn setup-tooltip!
  "Append <g class='tooltip'> to g-chart (idempotent).
   Also injects a drop-shadow filter into the root <svg>.
   Returns the tooltip <g> DOM node."
  [g-chart]
  (let [svg-node (.. (.node g-chart) -ownerSVGElement)
        svg      (.select d3 svg-node)]
    (.attr svg "overflow" "visible"))
  (let [existing (.select g-chart "g.chart-tooltip")]
    (if (.empty existing)
      (let [g (-> g-chart
                  (.append "g")
                  (.attr "class" "chart-tooltip")
                  (.attr "visibility" "hidden")
                  (.attr "pointer-events" "none"))]
        (-> g (.append "path") (.attr "class" "tooltip-bg"))
        (-> g (.append "g")    (.attr "class" "tooltip-content"))
        (.node g))
      (.node existing))))

(defn hide-tooltip! [tooltip-g]
  (-> (.select d3 tooltip-g)
      (.attr "visibility" "hidden")))

(defn- side-placement
  "Tooltip beside the anchor, on whichever side keeps it inside chart-width."
  [anchor-x anchor-y w h gap chart-width]
  (let [overflow 20
        x-right  (+ anchor-x gap)
        x-left   (- anchor-x w gap)
        on-left? (> (+ x-right w) chart-width)
        x        (if on-left? x-left x-right)
        y        (-> (- anchor-y (/ h 2)) (max (- overflow)))]
    {:x x :y y :side (if on-left? :right :left) :arrow-pos (- anchor-y y)}))

(defn- card-bounds
  "The enclosing dashboard card's box, in tooltip-g's parent coordinate frame
   (so it accounts for the svg's viewBox scaling). nil outside a card."
  [tooltip-g]
  (let [parent (.-parentNode tooltip-g)
        card   (.closest parent ".dashboard-item")
        ctm    (some-> (.getScreenCTM parent) (.inverse))]
    (when (and card ctm)
      (let [rect     (.getBoundingClientRect card)
            to-local (fn [x y]
                       (let [p (.matrixTransform (js/DOMPoint. x y) ctm)]
                         [(.-x p) (.-y p)]))
            [x1 y1]  (to-local (.-left rect) (.-top rect))
            [x2 y2]  (to-local (.-right rect) (.-bottom rect))]
        {:min-x x1 :min-y y1 :max-x x2 :max-y y2}))))

(defn- default-bounds [chart-width]
  (let [overflow 60]
    {:min-x (- overflow) :min-y (- overflow) :max-x (+ chart-width overflow)}))

(defn- above-placement
  "Tooltip centered above the anchor, arrow pointing down at it, shifted to
   stay inside bounds."
  [anchor-x anchor-y w h gap {:keys [min-x min-y max-x]}]
  (let [x (-> (- anchor-x (/ w 2)) (min (- max-x w)) (max min-x))
        y (-> (- anchor-y h gap) (max min-y))]
    {:x         x
     :y         y
     :side      :bottom
     :arrow-pos (- anchor-x x)}))

(defn show-tooltip!
  "Populate and position the tooltip relative to anchor-x/anchor-y.
   data = {:category str :rows [{:label :value :color}] :anchor-x num :anchor-y num
           :placement (:side | :above), default :side}"
  [tooltip-g {:keys [category rows anchor-x anchor-y placement text-centered?]} chart-width]
  (let [pad     8
        row-h   16
        title-h (if (seq category) 22 0)
        dot-r   4
        aw      12
        ah      10
        sel     (.select d3 tooltip-g)
        content (.select sel "g.tooltip-content")]

    (-> content (.selectAll "*") (.remove))

    (when (seq category)
      (-> content
          (.append "text")
          (.attr "class" "tooltip-title")
          (.attr "x" pad)
          (.attr "y" (+ pad 14))
          (.attr "font-size" "12px")
          (.text category)))

    (dorun
      (map-indexed
       (fn [i {:keys [label value color marker?]}]
         (let [row-y    (+ pad title-h (* i row-h))
               marker?  (not= false marker?)
               text-x   (+ pad (if marker? (+ (* dot-r 2) 4) 0))]
           (when marker?
             (-> content
                 (.append "circle")
                 (.attr "class" "tooltip-dot")
                 (.attr "cx" (+ pad dot-r))
                 (.attr "cy" (+ row-y (/ row-h 2)))
                 (.attr "r" dot-r)
                 (.attr "fill" color)))
           (-> content
               (.append "text")
               (.attr "class" "tooltip-row")
               (.attr "x" text-x)
               (.attr "y" (+ row-y (/ row-h 2) 4))
               (.attr "fill" color)
               (.attr "font-size" "11px")
               (.text (str label ": " value)))))
       rows))


    (let [bbox                         (.getBBox (.node content))
          w                            (max 50 (+ (.-width bbox) (* pad 2)))
          h                            (+ (.-height bbox) (* pad 2))
          gap                          12
          {:keys [x y side arrow-pos]} (if (= placement :above)
                                          (above-placement anchor-x anchor-y w h gap
                                                           (or (card-bounds tooltip-g)
                                                               (default-bounds chart-width)))
                                          (side-placement anchor-x anchor-y w h gap chart-width))]

      (when text-centered?
        (-> content
            (.selectAll "text.tooltip-row, text.tooltip-title")
            (.attr "text-anchor" "middle")
            (.attr "x" (/ w 2))))

      (-> (.select sel "path.tooltip-bg")
          (.attr "d" (tooltip-bg-path-d w h 10 aw ah arrow-pos side))
          (.attr "fill" "white")
          (.attr "stroke" "var(--pt-color-border-light)")
          (.attr "stroke-width" "1")
          (.attr "filter" "drop-shadow(0 0 2px var(--pt-color-border-light))"))

      (-> sel
          (.attr "transform" (str "translate(" x "," y ")"))
          (.attr "visibility" "visible")))))

(defn make-tooltip-api
  "Returns {:show! fn :hide! fn} for injection into render ctx."
  [tooltip-g chart-width]
  {:show! (fn [data] (show-tooltip! tooltip-g data chart-width))
   :hide! (fn []     (hide-tooltip! tooltip-g))})